import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/hooks/useNotify';
import {
  applyHaynesProDetailsPrefill,
  buildHaynesProDetails,
  candidateNeedsVerification,
  cloneHaynesProDetailsForm,
  emptyHaynesProDetailsForm,
  haynesProDetailsFormEquals,
  haynesProDetailsFormTouchedFromChange,
  haynesProUiLanguage,
  isAbortError,
  isStaleConflict,
  parseRepairTypeKey,
  parseTechnicalDataError,
  repairTypeKey,
  useVehicleTechnicalData,
  type HaynesProCandidate,
  type HaynesProCandidatesResponse,
  type HaynesProDetailsFormState,
  type HaynesProDetailsFormTouched,
  type HaynesProDetailsInput,
  type HaynesProLanguage,
  type HaynesProLookupMode,
  type HaynesProRepairTypesBody,
  type NormalizedRepairTimeType,
  type TechnicalDataErrorCode,
} from '@/hooks/useVehicleTechnicalData';
import { useAuthStore } from '@/stores/authStore';
import { HaynesProCandidateList } from './HaynesProCandidateList';
import { HaynesProDetailsForm } from './HaynesProDetailsForm';
import {
  knownTechnicalSnapshot,
  partitionPreferredCandidates,
  visibleHaynesProCandidates,
} from './knownTechnicalCandidates';

export interface VehicleTechnicalDataPanelProps {
  vehicleId: string | undefined;
  make?: string | null;
  model?: string | null;
  hasVin?: boolean;
}

interface LookupSession {
  vehicleId: string;
  language: HaynesProLanguage;
  mode: HaynesProLookupMode;
  details: HaynesProDetailsInput | undefined;
  detailsForm: HaynesProDetailsFormState;
  expectedSourceFingerprint: string;
  expectedCurrentVariantRevision: number;
}

function errorMessageKey(code: TechnicalDataErrorCode | 'unknown'): string {
  return `technicalData.errors.${code}`;
}

export function VehicleTechnicalDataPanel({
  vehicleId,
  make,
  model,
  hasVin = false,
}: VehicleTechnicalDataPanelProps) {
  const { t, i18n } = useTranslation('vehicles');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const language = haynesProUiLanguage(i18n.language);
  const canUpdateVehicle = useAuthStore((state) => state.hasPermission('vehicles.update'));
  const { capabilityQuery, candidatesMutation, repairTypesMutation, confirmMutation } =
    useVehicleTechnicalData(vehicleId);

  const requestGenerationRef = useRef(0);
  const typeRequestGenerationRef = useRef(0);
  const confirmOperationRef = useRef(0);
  const lookupPendingRef = useRef(false);
  const repairTypesPendingRef = useRef(false);
  const confirmPendingRef = useRef(false);
  const vehicleIdRef = useRef(vehicleId);
  const previousVehicleIdRef = useRef(vehicleId);
  const selectedCandidateIdRef = useRef<string | null>(null);
  const selectedCarTypeIdRef = useRef<number | null>(null);
  const touchedFieldsRef = useRef<HaynesProDetailsFormTouched>({});
  vehicleIdRef.current = vehicleId;

  const [details, setDetails] = useState<HaynesProDetailsFormState>(() =>
    emptyHaynesProDetailsForm(make, model),
  );
  const [showDetailsForm, setShowDetailsForm] = useState(!hasVin);
  const [lookup, setLookup] = useState<HaynesProCandidatesResponse | null>(null);
  const [lookupSession, setLookupSession] = useState<LookupSession | null>(null);
  const [lookupError, setLookupError] = useState<TechnicalDataErrorCode | 'unknown' | null>(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [selectedRepairKey, setSelectedRepairKey] = useState('');
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [lookupPending, setLookupPending] = useState(false);
  const [confirmPending, setConfirmPending] = useState(false);
  const [lookupInvalidated, setLookupInvalidated] = useState(false);
  const [loadedRepairTypes, setLoadedRepairTypes] = useState<readonly NormalizedRepairTimeType[] | null>(
    null,
  );
  const [loadedRepairTypesCandidateId, setLoadedRepairTypesCandidateId] = useState<string | null>(null);
  const [repairTypesPending, setRepairTypesPending] = useState(false);
  const [repairTypesError, setRepairTypesError] = useState<TechnicalDataErrorCode | 'unknown' | null>(
    null,
  );
  const [showOtherCandidates, setShowOtherCandidates] = useState(false);
  const [candidateFilter, setCandidateFilter] = useState('');

  useEffect(() => {
    if (!hasVin) setShowDetailsForm(true);
  }, [hasVin]);

  useEffect(() => {
    if (previousVehicleIdRef.current === vehicleId) return;
    previousVehicleIdRef.current = vehicleId;
    requestGenerationRef.current += 1;
    typeRequestGenerationRef.current += 1;
    confirmOperationRef.current += 1;
    lookupPendingRef.current = false;
    repairTypesPendingRef.current = false;
    confirmPendingRef.current = false;
    selectedCandidateIdRef.current = null;
    selectedCarTypeIdRef.current = null;
    touchedFieldsRef.current = {};
    setDetails(emptyHaynesProDetailsForm(make, model));
    setShowDetailsForm(!hasVin);
    setLookup(null);
    setLookupSession(null);
    setLookupError(null);
    setSelectedCandidateId(null);
    setSelectedRepairKey('');
    setReason('');
    setAcknowledged(false);
    setLookupPending(false);
    setConfirmPending(false);
    setLookupInvalidated(false);
    setLoadedRepairTypes(null);
    setLoadedRepairTypesCandidateId(null);
    setRepairTypesPending(false);
    setRepairTypesError(null);
    setShowOtherCandidates(false);
    setCandidateFilter('');
  }, [vehicleId, make, model, hasVin]);

  const capability = capabilityQuery.data;
  const currentFingerprint = capability?.currentSourceFingerprint;
  const currentRevision = capability?.confirmedVariant?.revision ?? 0;

  useEffect(() => {
    setDetails((prev) =>
      applyHaynesProDetailsPrefill({
        current: prev,
        knownDetails: capability?.knownDetails,
        touched: touchedFieldsRef.current,
        fallbackMake: make,
        fallbackModel: model,
      }),
    );
  }, [vehicleId, make, model, capability?.knownDetails]);

  const handleDetailsChange = (next: HaynesProDetailsFormState) => {
    setDetails((prev) => {
      touchedFieldsRef.current = haynesProDetailsFormTouchedFromChange(
        prev,
        next,
        touchedFieldsRef.current,
      );
      return next;
    });
  };

  useEffect(() => {
    if (!lookupSession) return;
    if (lookupSession.vehicleId !== vehicleId) return;
    const inputsChanged =
      language !== lookupSession.language ||
      !haynesProDetailsFormEquals(details, lookupSession.detailsForm);
    const capabilityChanged =
      currentFingerprint !== undefined &&
      (currentFingerprint !== lookupSession.expectedSourceFingerprint ||
        currentRevision !== lookupSession.expectedCurrentVariantRevision);
    if (!inputsChanged && !capabilityChanged) return;
    requestGenerationRef.current += 1;
    typeRequestGenerationRef.current += 1;
    lookupPendingRef.current = false;
    repairTypesPendingRef.current = false;
    selectedCandidateIdRef.current = null;
    selectedCarTypeIdRef.current = null;
    setLookup(null);
    setLookupSession(null);
    setSelectedCandidateId(null);
    setSelectedRepairKey('');
    setAcknowledged(false);
    setLookupError(null);
    setLookupPending(false);
    setLookupInvalidated(true);
    setLoadedRepairTypes(null);
    setLoadedRepairTypesCandidateId(null);
    setRepairTypesPending(false);
    setRepairTypesError(null);
    setShowOtherCandidates(false);
    setCandidateFilter('');
  }, [details, language, currentFingerprint, currentRevision, lookupSession, vehicleId]);

  const candidatePartition = useMemo(
    () =>
      lookup
        ? partitionPreferredCandidates(lookup.candidates, lookup.comparison)
        : { preferred: [], others: [], hasKnownTechnicalFields: false },
    [lookup],
  );
  const showCandidateFilter =
    (candidatePartition.preferred.length > 0 && !showOtherCandidates
      ? candidatePartition.preferred.length
      : (lookup?.candidates.length ?? 0)) > 5;
  const displayedCandidates = useMemo(
    () =>
      lookup
        ? visibleHaynesProCandidates(lookup.candidates, lookup.comparison, {
            showOthers: showOtherCandidates,
            filterQuery: showCandidateFilter ? candidateFilter : '',
          })
        : [],
    [lookup, showOtherCandidates, candidateFilter, showCandidateFilter],
  );
  const preferredIds = useMemo(
    () => new Set(candidatePartition.preferred.map((item) => item.id)),
    [candidatePartition.preferred],
  );
  const selectedCandidate = useMemo(
    () => displayedCandidates.find((item) => item.id === selectedCandidateId) ?? null,
    [displayedCandidates, selectedCandidateId],
  );
  const embeddedRepairTypes = selectedCandidate?.repairTimeTypes ?? [];
  const fetchedRepairTypes =
    selectedCandidate &&
    loadedRepairTypesCandidateId === selectedCandidate.id &&
    loadedRepairTypes !== null
      ? loadedRepairTypes
      : null;
  const resolvedRepairTypes =
    embeddedRepairTypes.length > 0 ? embeddedRepairTypes : (fetchedRepairTypes ?? []);
  const needsTypeFetch = Boolean(selectedCandidate && embeddedRepairTypes.length === 0);
  const typesReady = Boolean(selectedCandidate) && (!needsTypeFetch || fetchedRepairTypes !== null);
  const needsVerification = selectedCandidate
    ? candidateNeedsVerification(selectedCandidate.comparison)
    : false;
  const selectedRepair = selectedRepairKey ? parseRepairTypeKey(selectedRepairKey) : null;
  const selectedRepairAllowed =
    selectedRepair !== null &&
    resolvedRepairTypes.some((type) => repairTypeKey(type) === selectedRepairKey);
  const sessionMatchesInputs =
    lookupSession !== null &&
    lookupSession.vehicleId === vehicleId &&
    lookupSession.language === language &&
    haynesProDetailsFormEquals(details, lookupSession.detailsForm);
  const canConfirm =
    canUpdateVehicle &&
    sessionMatchesInputs &&
    Boolean(selectedCandidate) &&
    typesReady &&
    selectedRepairAllowed &&
    !repairTypesPending &&
    repairTypesError === null &&
    reason.trim().length > 0 &&
    (!needsVerification || acknowledged) &&
    !confirmPending &&
    !lookupPending;

  const resetTypeState = () => {
    typeRequestGenerationRef.current += 1;
    repairTypesPendingRef.current = false;
    setLoadedRepairTypes(null);
    setLoadedRepairTypesCandidateId(null);
    setRepairTypesPending(false);
    setRepairTypesError(null);
  };

  const resetSelection = () => {
    selectedCandidateIdRef.current = null;
    selectedCarTypeIdRef.current = null;
    setSelectedCandidateId(null);
    setSelectedRepairKey('');
    setAcknowledged(false);
    resetTypeState();
  };

  const resetCandidateListState = () => {
    setShowOtherCandidates(false);
    setCandidateFilter('');
  };

  const applyLookup = (result: HaynesProCandidatesResponse, session: LookupSession) => {
    setLookup(result);
    setLookupSession(session);
    setLookupError(null);
    setLookupInvalidated(false);
    resetSelection();
    resetCandidateListState();
    if (result.noData) {
      setShowDetailsForm(true);
    } else if (session.mode === 'vin') {
      setShowDetailsForm(false);
    }
  };

  const captureLookupSession = (mode: HaynesProLookupMode): LookupSession | null => {
    if (!vehicleId || !capability) return null;
    const detailsPayload = buildHaynesProDetails(details);
    return {
      vehicleId,
      language,
      mode,
      details: mode === 'details' ? detailsPayload : undefined,
      detailsForm: cloneHaynesProDetailsForm(details),
      expectedSourceFingerprint: capability.currentSourceFingerprint,
      expectedCurrentVariantRevision: capability.confirmedVariant?.revision ?? 0,
    };
  };

  const runLookup = async (mode: HaynesProLookupMode) => {
    if (!vehicleId || confirmPendingRef.current) return;
    const session = captureLookupSession(mode);
    if (!session) return;

    requestGenerationRef.current += 1;
    const generation = requestGenerationRef.current;
    lookupPendingRef.current = true;
    setLookupPending(true);
    setLookup(null);
    setLookupSession(session);
    setLookupError(null);
    setLookupInvalidated(false);
    resetSelection();
    resetCandidateListState();
    if (mode === 'vin') {
      setShowDetailsForm(false);
    }

    try {
      const result = await candidatesMutation.mutateAsync({
        mode,
        language: session.language,
        ...(mode === 'details' && session.details ? { details: session.details } : {}),
      });
      if (generation !== requestGenerationRef.current) return;
      if (session.vehicleId !== vehicleIdRef.current) return;
      applyLookup(result, session);
    } catch (error) {
      if (generation !== requestGenerationRef.current) return;
      if (isAbortError(error)) return;
      const parsed = parseTechnicalDataError(error);
      setLookupError(parsed.code);
      setLookupSession(null);
      if (parsed.code === 'source_missing' || parsed.code === 'no_data' || parsed.code === 'invalid_details') {
        setShowDetailsForm(true);
      }
      notify.error(tc('error'), t(errorMessageKey(parsed.code)));
    } finally {
      if (generation === requestGenerationRef.current) {
        lookupPendingRef.current = false;
        setLookupPending(false);
      }
    }
  };

  const typeResponseStillCurrent = (
    typeGeneration: number,
    lookupGeneration: number,
    candidate: HaynesProCandidate,
    requestVehicleId: string,
  ) =>
    typeGeneration === typeRequestGenerationRef.current &&
    lookupGeneration === requestGenerationRef.current &&
    requestVehicleId === vehicleIdRef.current &&
    selectedCandidateIdRef.current === candidate.id &&
    selectedCarTypeIdRef.current === candidate.carTypeId;

  const buildRepairTypesBody = (
    candidate: HaynesProCandidate,
    result: HaynesProCandidatesResponse,
    session: LookupSession,
  ): HaynesProRepairTypesBody => {
    const method = result.lookupMethod;
    return {
      carTypeId: candidate.carTypeId,
      language: session.language,
      method,
      expectedSourceFingerprint: session.expectedSourceFingerprint,
      ...((method === 'details' || method === 'tree') && session.details
        ? { details: session.details }
        : {}),
    };
  };

  const loadRepairTypes = async (
    candidate: HaynesProCandidate,
    result: HaynesProCandidatesResponse,
    session: LookupSession,
  ) => {
    typeRequestGenerationRef.current += 1;
    const typeGeneration = typeRequestGenerationRef.current;
    const lookupGeneration = requestGenerationRef.current;
    const requestVehicleId = session.vehicleId;
    repairTypesPendingRef.current = true;
    setLoadedRepairTypes(null);
    setLoadedRepairTypesCandidateId(null);
    setRepairTypesError(null);
    setRepairTypesPending(true);

    try {
      const response = await repairTypesMutation.mutateAsync(
        buildRepairTypesBody(candidate, result, session),
      );
      if (!typeResponseStillCurrent(typeGeneration, lookupGeneration, candidate, requestVehicleId)) {
        return;
      }
      if (response.carTypeId !== candidate.carTypeId) return;
      setLoadedRepairTypes(response.repairTimeTypes);
      setLoadedRepairTypesCandidateId(candidate.id);
    } catch (error) {
      if (!typeResponseStillCurrent(typeGeneration, lookupGeneration, candidate, requestVehicleId)) {
        return;
      }
      if (isAbortError(error)) return;
      const parsed = parseTechnicalDataError(error);
      if (isStaleConflict(parsed.code)) {
        requestGenerationRef.current += 1;
        typeRequestGenerationRef.current += 1;
        repairTypesPendingRef.current = false;
        setLookup(null);
        setLookupSession(null);
        resetSelection();
        setLookupError(null);
        setLookupPending(false);
        setLookupInvalidated(false);
        setRepairTypesPending(false);
        await capabilityQuery.refetch();
        notify.warning(t('technicalData.stale'), t('technicalData.staleHint'));
        return;
      }
      setRepairTypesError(parsed.code);
      notify.error(tc('error'), t(errorMessageKey(parsed.code)));
    } finally {
      if (typeGeneration === typeRequestGenerationRef.current) {
        repairTypesPendingRef.current = false;
        setRepairTypesPending(false);
      }
    }
  };

  const handleSelectCandidate = (candidateId: string) => {
    if (confirmPendingRef.current) return;
    const next = lookup?.candidates.find((item) => item.id === candidateId) ?? null;
    selectedCandidateIdRef.current = candidateId;
    selectedCarTypeIdRef.current = next?.carTypeId ?? null;
    setSelectedCandidateId(candidateId);
    setSelectedRepairKey('');
    setAcknowledged(false);
    resetTypeState();
    if (next && next.repairTimeTypes.length === 0 && lookup && lookupSession) {
      void loadRepairTypes(next, lookup, lookupSession);
    }
  };

  const handleRetryRepairTypes = () => {
    if (confirmPendingRef.current || repairTypesPendingRef.current) return;
    if (!selectedCandidate || !lookup || !lookupSession) return;
    if (selectedCandidate.repairTimeTypes.length > 0) return;
    void loadRepairTypes(selectedCandidate, lookup, lookupSession);
  };

  const handleConfirm = async () => {
    if (!vehicleId || !lookup || !lookupSession || !selectedCandidate || !selectedRepair) return;
    if (!selectedRepairAllowed || repairTypesPendingRef.current) return;
    if (lookupPendingRef.current || confirmPendingRef.current) return;
    if (!canUpdateVehicle) return;
    if (lookupSession.vehicleId !== vehicleId) return;
    if (lookupSession.language !== language) return;
    if (!haynesProDetailsFormEquals(details, lookupSession.detailsForm)) return;

    const trimmedReason = reason.trim();
    if (!trimmedReason) return;
    if (needsVerification && !acknowledged) return;

    const detailsPayload =
      lookup.lookupMethod === 'details' || lookup.lookupMethod === 'tree'
        ? lookupSession.details
        : undefined;

    const confirmVehicleId = vehicleId;
    confirmOperationRef.current += 1;
    const confirmOp = confirmOperationRef.current;
    confirmPendingRef.current = true;
    setConfirmPending(true);

    const confirmStillCurrent = () =>
      confirmOp === confirmOperationRef.current && confirmVehicleId === vehicleIdRef.current;

    try {
      await confirmMutation.mutateAsync({
        confirmed: true,
        reason: trimmedReason.slice(0, 200),
        language: lookupSession.language,
        method: lookup.lookupMethod,
        ...(detailsPayload ? { details: detailsPayload } : {}),
        carTypeId: selectedCandidate.carTypeId,
        repairtimeTypeId: selectedRepair.repairtimeTypeId,
        typeCategory: selectedRepair.typeCategory,
        rootNodeId: selectedRepair.rootNodeId,
        candidateId: selectedCandidate.id,
        expectedSourceFingerprint: lookupSession.expectedSourceFingerprint,
        expectedCurrentVariantRevision: lookupSession.expectedCurrentVariantRevision,
      });
      if (!confirmStillCurrent()) return;
      notify.success(t('technicalData.success'), t('technicalData.successDesc'));
      setLookup(null);
      setLookupSession(null);
      resetSelection();
      setReason('');
      setLookupInvalidated(false);
    } catch (error) {
      if (!confirmStillCurrent()) return;
      if (isAbortError(error)) return;
      const parsed = parseTechnicalDataError(error);
      if (isStaleConflict(parsed.code)) {
        setLookup(null);
        setLookupSession(null);
        resetSelection();
        setLookupError(null);
        confirmPendingRef.current = false;
        setConfirmPending(false);
        await capabilityQuery.refetch();
        notify.warning(t('technicalData.stale'), t('technicalData.staleHint'));
        return;
      }
      notify.error(tc('error'), t(errorMessageKey(parsed.code)));
    } finally {
      if (confirmOp === confirmOperationRef.current) {
        confirmPendingRef.current = false;
        setConfirmPending(false);
      }
    }
  };

  if (!vehicleId) return null;

  if (capabilityQuery.isPending) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }} role="status" aria-label={t('technicalData.pending')}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  if (capabilityQuery.isError) {
    const parsed = parseTechnicalDataError(capabilityQuery.error);
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Alert severity="error" role="alert">
          {t(errorMessageKey(parsed.code))}
        </Alert>
      </Paper>
    );
  }

  if (!capability || (!capability.available && capability.reason === 'disabled')) {
    return null;
  }

  if (!capability.available) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }} component="section" aria-labelledby="haynespro-technical-data-title">
        <Typography id="haynespro-technical-data-title" variant="subtitle1" fontWeight={600} gutterBottom>
          {t('technicalData.title')}
        </Typography>
        <Alert severity="info" role="status">
          {t('technicalData.unavailable')}
        </Alert>
      </Paper>
    );
  }

  const confirmed = capability.confirmedVariant;
  const lookingUp = lookupPending;
  const confirming = confirmPending;
  const searchDisabled = lookingUp || confirming;

  return (
    <Paper variant="outlined" sx={{ p: 2 }} component="section" aria-labelledby="haynespro-technical-data-title">
      <Stack spacing={2}>
        <Box>
          <Typography id="haynespro-technical-data-title" variant="subtitle1" fontWeight={600}>
            {t('technicalData.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('technicalData.needsVerification')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('technicalData.workOrderTimeHint')}
          </Typography>
        </Box>

        {!canUpdateVehicle && (
          <Alert severity="info" role="status">
            {t('technicalData.confirmPermissionDenied')}
          </Alert>
        )}

        {capability.isStale && confirmed && (
          <Alert
            severity="warning"
            role="status"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void capabilityQuery.refetch()}
                aria-label={t('technicalData.staleReload')}
              >
                {t('technicalData.staleReload')}
              </Button>
            }
          >
            {t('technicalData.staleHint')}
          </Alert>
        )}

        {confirmed && !capability.isStale && (
          <Alert severity={confirmed.usable ? 'success' : 'info'} role="status">
            <Typography variant="body2" fontWeight={600}>
              {t('technicalData.existingConfirmation')}
            </Typography>
            <Typography variant="body2">
              {t('technicalData.confirmedSummary', {
                carTypeId: confirmed.carTypeId,
                repairtimeTypeId: confirmed.repairtimeTypeId,
              })}
            </Typography>
            <Typography variant="caption" display="block">
              {t('technicalData.confirmedReason')}: {confirmed.confirmationReason}
            </Typography>
            <Typography variant="caption" display="block">
              {t('technicalData.confirmedAt')}: {new Date(confirmed.confirmedAt).toLocaleString(i18n.language)}
            </Typography>
          </Alert>
        )}

        {confirmed && capability.isStale && (
          <Typography variant="body2" color="text.secondary">
            {t('technicalData.needsNewConfirmation')}
          </Typography>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {hasVin && (
            <Button
              variant="contained"
              onClick={() => void runLookup('vin')}
              disabled={searchDisabled}
              aria-label={t('technicalData.searchByVinAria')}
            >
              {lookingUp ? tc('loading') : t('technicalData.searchByVin')}
            </Button>
          )}
          {!showDetailsForm && (
            <Button
              variant="outlined"
              onClick={() => setShowDetailsForm(true)}
              disabled={confirming}
              aria-label={t('technicalData.showDetailsFormAria')}
            >
              {t('technicalData.showDetailsForm')}
            </Button>
          )}
        </Stack>

        {lookingUp && (
          <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">{t('technicalData.pending')}</Typography>
          </Box>
        )}

        {lookupError && (
          <Alert severity="error" role="alert">
            {t(errorMessageKey(lookupError))}
          </Alert>
        )}

        {lookupInvalidated && !lookup && (
          <Alert severity="info" role="status">
            {t('technicalData.lookupInvalidated')}
          </Alert>
        )}

        {!hasVin && !lookup && (
          <Alert severity="info" role="status">
            {t('technicalData.missingVinHint')}
          </Alert>
        )}

        {lookup?.noData && (
          <Alert severity="info" role="status">
            {t('technicalData.noDataHint')}
          </Alert>
        )}

        {lookup && !lookup.noData && lookup.autoSelected === false && lookup.candidates.length === 1 && (
          <Alert severity="info" role="status">
            {t('technicalData.uniqueStillNeedsConfirm')}
          </Alert>
        )}

        {lookup &&
          candidatePartition.preferred.length === 1 &&
          lookup.candidates.length > 1 && (
            <Alert severity="info" role="status">
              {t('technicalData.preferredStillNeedsConfirm')}
            </Alert>
          )}

        {lookup &&
          candidatePartition.hasKnownTechnicalFields &&
          candidatePartition.preferred.length === 0 &&
          lookup.candidates.length > 0 && (
            <Alert severity="info" role="status">
              {t('technicalData.noPreferredHint')}
            </Alert>
          )}

        {lookup &&
          !candidatePartition.hasKnownTechnicalFields &&
          lookup.candidates.length > 1 && (
            <Alert severity="info" role="status">
              {t('technicalData.noKnownTechnicalHint')}
            </Alert>
          )}

        {lookup && lookup.yearComparisonBasis === 'unknown' && lookup.candidates.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t('technicalData.yearUnknownHint')}
          </Typography>
        )}

        {lookup && lookup.candidates.length > 0 && (
          <HaynesProCandidateList
            candidates={displayedCandidates}
            preferredIds={preferredIds}
            preferredCount={candidatePartition.preferred.length}
            totalCount={lookup.candidates.length}
            otherCount={candidatePartition.others.length}
            knownTechnical={knownTechnicalSnapshot(lookup.comparison)}
            filterQuery={candidateFilter}
            onFilterQueryChange={setCandidateFilter}
            showFilter={showCandidateFilter}
            showOthers={showOtherCandidates}
            onShowOthersChange={(show) => {
              setShowOtherCandidates(show);
              if (!show) setCandidateFilter('');
            }}
            showOthersToggle={
              candidatePartition.preferred.length > 0 && candidatePartition.others.length > 0
            }
            selectedCandidateId={selectedCandidate?.id ?? null}
            onSelect={handleSelectCandidate}
            disabled={confirming}
          />
        )}

        {selectedCandidate && (
          <RepairTypePicker
            types={resolvedRepairTypes}
            selectedKey={selectedRepairKey}
            onSelect={setSelectedRepairKey}
            disabled={confirming}
            pending={needsTypeFetch && repairTypesPending}
            error={needsTypeFetch ? repairTypesError : null}
            onRetry={handleRetryRepairTypes}
          />
        )}

        {selectedCandidate && (
          <Stack spacing={1.5}>
            {needsVerification && (
              <Alert severity="warning" role="status">
                {selectedCandidate.comparison.engineCode === 'mismatch' ||
                selectedCandidate.comparison.make === 'mismatch' ||
                selectedCandidate.comparison.model === 'mismatch' ||
                selectedCandidate.comparison.year === 'mismatch' ||
                selectedCandidate.comparison.capacity === 'mismatch' ||
                selectedCandidate.comparison.output === 'mismatch'
                  ? t('technicalData.mismatchExplanation')
                  : t('technicalData.missingExplanation')}
              </Alert>
            )}
            <TextField
              label={t('technicalData.reason')}
              helperText={t('technicalData.reasonHint')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required
              disabled={confirming}
              multiline
              minRows={2}
              slotProps={{
                htmlInput: {
                  maxLength: 200,
                  'aria-required': true,
                  'aria-label': t('technicalData.reason'),
                },
              }}
            />
            {needsVerification && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledged}
                    onChange={(event) => setAcknowledged(event.target.checked)}
                    disabled={confirming}
                    inputProps={{ 'aria-label': t('technicalData.mismatchAckAria') }}
                  />
                }
                label={t('technicalData.mismatchAckLabel')}
              />
            )}
            <Box>
              <Button
                variant="contained"
                onClick={() => void handleConfirm()}
                disabled={!canConfirm}
                aria-label={t('technicalData.confirmAria')}
              >
                {confirming ? tc('loading') : t('technicalData.confirmAction')}
              </Button>
            </Box>
          </Stack>
        )}

        {showDetailsForm && (
          <HaynesProDetailsForm
            value={details}
            onChange={handleDetailsChange}
            onSearch={() => void runLookup('details')}
            isSearching={lookingUp}
            disabled={confirming}
          />
        )}
      </Stack>
    </Paper>
  );
}

function RepairTypePicker({
  types,
  selectedKey,
  onSelect,
  disabled = false,
  pending = false,
  error = null,
  onRetry,
}: {
  types: readonly NormalizedRepairTimeType[];
  selectedKey: string;
  onSelect: (key: string) => void;
  disabled?: boolean;
  pending?: boolean;
  error?: TechnicalDataErrorCode | 'unknown' | null;
  onRetry?: () => void;
}) {
  const { t } = useTranslation('vehicles');

  if (pending) {
    return (
      <Box
        role="status"
        aria-live="polite"
        aria-label={t('technicalData.repairTypesPending')}
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <CircularProgress size={18} />
        <Typography variant="body2">{t('technicalData.repairTypesPending')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        role="alert"
        action={
          onRetry ? (
            <Button
              color="inherit"
              size="small"
              onClick={onRetry}
              disabled={disabled}
              aria-label={t('technicalData.retryRepairTypesAria')}
            >
              {t('technicalData.retryRepairTypes')}
            </Button>
          ) : undefined
        }
      >
        {t(errorMessageKey(error))}
      </Alert>
    );
  }

  if (types.length === 0) {
    return (
      <Alert severity="warning" role="status">
        {t('technicalData.noRepairTypes')}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {t('technicalData.selectRepairType')}
      </Typography>
      <RadioGroup
        aria-label={t('technicalData.selectRepairType')}
        value={selectedKey || 'none'}
        onChange={(event) => {
          if (!disabled) onSelect(event.target.value);
        }}
      >
        <Stack spacing={1}>
          {types.map((type) => {
            const key = repairTypeKey(type);
            const label = [
              type.type,
              type.typeCategory,
              type.madeFrom || type.madeUntil
                ? `${type.madeFrom ?? '…'}–${type.madeUntil ?? '…'}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <FormControlLabel
                key={key}
                value={key}
                control={
                  <Radio
                    size="small"
                    disabled={disabled}
                    inputProps={{
                      'aria-label': t('technicalData.repairTypeOption', {
                        label: label || String(type.repairtimeTypeId),
                      }),
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2">{label || String(type.repairtimeTypeId)}</Typography>
                    <Chip size="small" variant="outlined" label={type.typeCategory} />
                  </Box>
                }
              />
            );
          })}
        </Stack>
      </RadioGroup>
    </Box>
  );
}
